import { useState } from 'react';
import {
  X,
  Sparkles,
  Bug,
  RefreshCw,
  FileText,
  Wrench,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';
import { useChangelog } from '@/hooks/useChangelog';
import type { ChangeType } from '@/data/changelog/types';

const TYPE_CONFIG: Record<
  ChangeType,
  { label: string; icon: typeof Sparkles; color: string; dotColor: string }
> = {
  feat: {
    label: 'Novo',
    icon: Sparkles,
    color: 'text-emerald-600 dark:text-emerald-400',
    dotColor: 'bg-emerald-500',
  },
  fix: {
    label: 'Correção',
    icon: Bug,
    color: 'text-amber-600 dark:text-amber-400',
    dotColor: 'bg-amber-500',
  },
  refactor: {
    label: 'Refatoração',
    icon: RefreshCw,
    color: 'text-sky-600 dark:text-sky-400',
    dotColor: 'bg-sky-500',
  },
  docs: {
    label: 'Docs',
    icon: FileText,
    color: 'text-violet-600 dark:text-violet-400',
    dotColor: 'bg-violet-500',
  },
  chore: {
    label: 'Infra',
    icon: Wrench,
    color: 'text-muted-foreground',
    dotColor: 'bg-gray-400',
  },
};

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangelogModal({ isOpen, onClose }: ChangelogModalProps) {
  const { changelog } = useChangelog();
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  if (!isOpen) return null;

  // Popup user-facing: só versões com itens feat/fix amigáveis.
  const visibleVersions = changelog
    .map((ver) => ({
      ...ver,
      changes: ver.changes.filter(
        (c) => (c.type === 'feat' || c.type === 'fix') && c.user_description,
      ),
    }))
    .filter((ver) => ver.changes.length > 0);
  const effectiveExpanded =
    expandedVersion ?? visibleVersions[0]?.version ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl bg-popover border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-5 pb-4 border-b border-border">
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="absolute top-4 right-4 p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center shadow-md">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                O que há de novo
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Atualizações e melhorias do TMF9
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {/* Modal popup é "user-facing" — filtra agressivamente:
              - Só versões que têm pelo menos 1 item user_description
              - Só tipos feat + fix (sem chore/refactor/docs internos)
              - Esconde issue tags, paths, nomes de função (vivem no /changelog completo) */}
          {visibleVersions.map((ver, idx) => {
            const isExpanded = effectiveExpanded === ver.version;
            const isLatest = idx === 0;
            const feats = ver.changes.filter((c) => c.type === 'feat');
            const fixes = ver.changes.filter((c) => c.type === 'fix');
            const others: typeof ver.changes = [];
            const summaryToShow = ver.user_summary || ver.summary;

            return (
              <div key={ver.version} className="group">
                <button
                  onClick={() =>
                    setExpandedVersion(isExpanded ? null : ver.version)
                  }
                  className={`w-full px-3 py-2.5 rounded-xl flex items-center gap-3 transition-colors duration-150 ${
                    isExpanded
                      ? 'bg-muted'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-lg text-[11px] font-mono font-semibold ${
                      isLatest
                        ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300 ring-1 ring-primary-500/30'
                        : 'bg-muted text-muted-foreground ring-1 ring-border'
                    }`}
                  >
                    {ver.version}
                  </span>

                  <span className="flex-1 text-left text-sm text-foreground truncate">
                    {summaryToShow}
                  </span>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {new Date(ver.date + 'T12:00:00').toLocaleDateString(
                        'pt-BR',
                        { day: '2-digit', month: 'short' },
                      )}
                      {ver.time && (
                        <span className="ml-1 text-muted-foreground">
                          {ver.time}
                        </span>
                      )}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-150 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {isExpanded && (
                  <div className="mx-3 mt-1 mb-2 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                    {feats.length > 0 && (
                      <ChangeSection
                        label="Novidades"
                        type="feat"
                        changes={feats}
                      />
                    )}
                    {fixes.length > 0 && (
                      <ChangeSection
                        label="Correções"
                        type="fix"
                        changes={fixes}
                      />
                    )}
                    {others.length > 0 && (
                      <ChangeSection
                        label="Outros"
                        type="chore"
                        changes={others}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 flex items-center justify-between border-t border-border">
          <span className="text-xs text-muted-foreground">
            {changelog.length} versões
          </span>
          <a
            href="/changelog"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            Ver completo
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

function ChangeSection({
  label,
  type,
  changes,
}: {
  label: string;
  type: ChangeType;
  changes: Array<{
    type: ChangeType;
    module?: string;
    description: string;
    user_description?: string;
    issue?: string;
  }>;
}) {
  const config = TYPE_CONFIG[type];

  return (
    <div className="pl-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
        <span
          className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}
        >
          {label}
        </span>
      </div>
      <div className="space-y-1 pl-3 border-l border-border">
        {changes.map((c, i) => {
          const itemConfig = TYPE_CONFIG[c.type];
          const Icon = itemConfig.icon;
          // No popup user-facing, sempre prefere user_description; sem issue tag, sem module técnico
          return (
            <div key={i} className="flex items-start gap-2 py-1">
              <Icon
                className={`w-3.5 h-3.5 mt-0.5 ${itemConfig.color} opacity-80 shrink-0`}
              />
              <span className="text-sm text-foreground leading-relaxed">
                {c.user_description || c.description}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ChangelogModal;
